import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/DriverEdit.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { syncCarFromDriverProfile } from "../utils/driverCarSync";

export default function DriverEdit() {
  const { id } = useParams(); // profiles.id
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    role: "driver",
    email: "",
    driver_name: "",
    hire_date: "",
    phone: "",
    car_number: "",
    company: "",
    company_name: "",
  });

  // ✅ 원래 이메일 별도 보관 (연쇄 업데이트용)
  const [originalEmail, setOriginalEmail] = useState("");
  const [originalCarNumber, setOriginalCarNumber] = useState("");
  const [originalDriverName, setOriginalDriverName] = useState("");

  const handleChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  };

  const fetchDriver = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, role, email, driver_name, hire_date, phone, car_number, company, company_name")
        .eq("id", id)
        .eq("company", localStorage.getItem("email") || "")
        .single();

      if (error) throw error;
      if (!data) throw new Error("데이터가 없습니다.");

      setForm({
        role: data.role || "driver",
        email: data.email || "",
        driver_name: data.driver_name || "",
        hire_date: data.hire_date || "",
        phone: data.phone || "",
        car_number: data.car_number || "",
        company: data.company || "",
        company_name: data.company_name || localStorage.getItem("companyName") || data.company || "",
      });
      setOriginalEmail(data.email || "");
      setOriginalCarNumber(data.car_number || "");
      setOriginalDriverName(data.driver_name || "");
    } catch (e) {
      showUserAlert("조회 실패: " + e.message);
      navigate("/drivers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchDriver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isValidEmail = (v) =>
    typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const normalizeCarNumber = (v) => `${v || ""}`.trim();

  const handleSubmit = async () => {
    if (saving) return;

    const email = (form.email || "").trim();
    const phone = (form.phone || "").trim();
    const driverName = (form.driver_name || "").trim();

    if (!driverName) return showUserAlert("기사명을 입력하세요.");
    if (!phone) return showUserAlert("핸드폰 번호를 입력하세요.");
    if (email && !isValidEmail(email)) return showUserAlert("올바른 이메일 형식이 아닙니다.");

    setSaving(true);
    try {
      // 1) profiles 업데이트
      const payload = {
        role: "driver",
        email: email || null,
        driver_name: driverName,
        hire_date: form.hire_date || null,
        phone,
        car_number: form.car_number || null,
        company: form.company || null,
        company_name: form.company_name || localStorage.getItem("companyName") || form.company || null,
      };

      const { error: profErr } = await supabase
        .from("profiles")
        .update(payload)
        .eq("id", id)
        .eq("company", form.company || localStorage.getItem("email") || "");

      if (profErr) throw profErr;

      // 2) 이메일 변경 시 연쇄 업데이트
      const oldEmail = originalEmail;
      const newEmail = email;
      const company = form.company || localStorage.getItem("email") || null;
      const nextCarNumber = normalizeCarNumber(form.car_number);
      const prevCarNumber = normalizeCarNumber(originalCarNumber);

      await syncCarFromDriverProfile(supabase, {
        company,
        previousCarNumber: prevCarNumber,
        previousEmail: originalEmail,
        previousDriverName: originalDriverName,
        nextCarNumber,
        driverName,
        phone,
        email,
      });

      if (oldEmail && oldEmail !== newEmail) {
        // 각 테이블 driver_email 갱신 (회사 범위 한정)
        const updateEmailInTable = async (table) => {
          let q = supabase.from(table).update({ driver_email: newEmail || null }).eq("driver_email", oldEmail);
          if (company) q = q.eq("company", company);
          const { error } = await q;
          if (error) throw new Error(`${table}: ${error.message}`);
        };

        const results = await Promise.allSettled([
          updateEmailInTable("cars"),
          updateEmailInTable("maintenance"),
          updateEmailInTable("receipts"),
          updateEmailInTable("salary"),
        ]);

        const failed = results
          .map((r, i) => ({ r, table: ["cars", "maintenance", "receipts", "salary"][i] }))
          .filter((x) => x.r.status === "rejected");

        if (failed.length > 0) {
          const msg = failed.map((f) => `- ${f.table}`).join("\n");
          showUserAlert(
            "저장은 완료되었지만, 아래 테이블에서 이메일 연쇄 업데이트가 일부 실패했습니다:\n" + msg
          );
        } else {
          showUserAlert("저장 및 관련 테이블 이메일이 모두 업데이트되었습니다.");
        }

        // 원본 이메일 갱신
        setOriginalEmail(newEmail);
        setOriginalCarNumber(nextCarNumber);
        setOriginalDriverName(driverName);
      } else {
        showUserAlert("저장되었습니다.");
      }

      navigate("/drivers");
    } catch (e) {
      showUserAlert("저장 실패: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const ok = window.confirm("정말 이 기사를 삭제하시겠습니까?");
    if (!ok) return;

    setDeleting(true);
    try {
      const company = form.company || localStorage.getItem("email") || "";
      const driverName = (form.driver_name || "").trim();
      const phone = (form.phone || "").trim();
      const email = (form.email || "").trim();

      if (form.car_number || driverName || phone || email) {
        let carQuery = supabase.from("cars").update({
          driver: null,
          driver_email: null,
          phone: null,
        });
        if (company) carQuery = carQuery.eq("company", company);

        if (form.car_number) {
          carQuery = carQuery.or(
            `car_number.eq.${form.car_number},car_number_only.eq.${form.car_number},car_number.ilike.%${form.car_number}%`
          );
        } else if (email) {
          carQuery = carQuery.eq("driver_email", email);
        } else if (phone) {
          carQuery = carQuery.eq("phone", phone);
        } else if (driverName) {
          carQuery = carQuery.eq("driver", driverName);
        }

        const { error: carError } = await carQuery;
        if (carError) throw new Error(`차량 연결 해제 실패: ${carError.message}`);
      }

      let deleteQuery = supabase
        .from("profiles")
        .delete()
        .eq("id", id)
        .eq("role", "driver");
      if (company) deleteQuery = deleteQuery.eq("company", company);

      const { data: deletedRows, error } = await deleteQuery.select("id");
      if (error) throw error;
      if (!deletedRows || deletedRows.length === 0) {
        throw new Error("profiles에서 삭제된 행이 없습니다. RLS 삭제 권한 또는 회사 정보(company)를 확인해 주세요.");
      }

      showUserAlert("기사 정보가 삭제되었습니다.");
      navigate("/drivers");
    } catch (e) {
      showUserAlert("삭제 실패: " + e.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Header />
        <div className="p-4">불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Header />

      <div className="p-4 space-y-4">
        <h2 className="text-lg font-semibold">기사 정보 수정</h2>

        <Card className="p-4">
          <CardContent className="bg-white space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block font-semibold mb-1">회사</label>
                <Input value={form.company_name || form.company || ""} disabled />
              </div>

              <div>
                <label className="block font-semibold mb-1">역할</label>
                <Input value={form.role || "driver"} disabled />
              </div>

              {/* 이메일 수정 가능 */}
              <div>
                <label className="block font-semibold mb-1">기사 이메일</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  disabled={saving}
                  placeholder="선택 입력"
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">기사명</label>
                <Input
                  value={form.driver_name}
                  onChange={(e) => handleChange("driver_name", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">입사일</label>
                <Input
                  type="date"
                  value={form.hire_date || ""}
                  onChange={(e) => handleChange("hire_date", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">핸드폰</label>
                <Input
                  value={form.phone || ""}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  disabled={saving}
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">버스번호</label>
                <Input
                  value={form.car_number || ""}
                  onChange={(e) => handleChange("car_number", e.target.value)}
                  disabled={saving}
                  placeholder="예) 3263"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? "저장중..." : "저장"}
              </Button>
              <Button variant="outline" onClick={() => navigate("/drivers")} disabled={saving}>
                돌아가기
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting || saving}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {deleting ? "삭제중..." : "삭제"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
